import { 
  collection, 
  doc, 
  getDoc,
  getDocs,
  updateDoc,
  query, 
  orderBy,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './config';

/**
 * Get stock batches for an item (FIFO order)
 */
export const getStockBatches = async (itemId) => {
  try {
    const itemRef = doc(db, 'inventory', itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (itemSnap.exists()) {
      const batches = itemSnap.data().batches || [];
      // Sort by date (oldest first for FIFO)
      return {
        success: true,
        data: batches.sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate))
      };
    }
    
    return { success: false, error: 'Item not found', data: [] };
  } catch (error) {
    console.error('Error getting stock batches:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Add a new stock batch (from GRN transfer)
 */
export const addStockBatch = async (itemId, batchData) => {
  try {
    const itemRef = doc(db, 'inventory', itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (!itemSnap.exists()) {
      return { success: false, error: 'Item not found' };
    }
    
    const itemData = itemSnap.data();
    const batches = itemData.batches || [];
    
    // Create new batch with per-batch pricing
    // Use Date.now() + random suffix to prevent duplicate batchIds during bulk/parallel operations
    const newBatch = {
      batchId: `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      purchaseInvoiceId: batchData.purchaseInvoiceId,
      purchaseInvoiceNumber: batchData.purchaseInvoiceNumber,
      transferNumber: batchData.transferNumber || '',
      purchaseDate: batchData.purchaseDate,
      costPrice: batchData.costPrice,
      retailPrice: batchData.retailPrice || 0,
      wholesalePrice: batchData.wholesalePrice || 0,
      discount: batchData.discount || 0, // Discount percentage
      quantityPurchased: batchData.quantity,
      quantityRemaining: batchData.quantity,
      supplier: batchData.supplier || 'N/A',
      source: batchData.source || 'purchase',
      createdAt: new Date().toISOString()
    };
    
    batches.push(newBatch);
    
    // Calculate new average cost
    const totalStock = batches.reduce((sum, b) => sum + b.quantityRemaining, 0);
    const totalCost = batches.reduce((sum, b) => sum + (b.costPrice * b.quantityRemaining), 0);
    const averageCost = totalStock > 0 ? totalCost / totalStock : 0;
    
    // Update inventory
    await updateDoc(itemRef, {
      batches: batches,
      stock: totalStock,
      averageCost: averageCost,
      lastCostPrice: batchData.costPrice,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, batchId: newBatch.batchId };
  } catch (error) {
    console.error('Error adding stock batch:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deduct stock using FIFO method
 * Returns array of batches used with quantities
 */
export const deductStockFIFO = async (itemId, quantityToDeduct) => {
  try {
    const itemRef = doc(db, 'inventory', itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (!itemSnap.exists()) {
      return { success: false, error: 'Item not found', batchesUsed: [] };
    }
    
    const itemData = itemSnap.data();
    let batches = itemData.batches || [];
    let remainingToDeduct = quantityToDeduct;
    const batchesUsed = [];
    
    // Sort by createdAt timestamp (oldest first for FIFO) - more precise than purchaseDate
    batches = batches.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    
    // Deduct from oldest batches first
    for (let i = 0; i < batches.length && remainingToDeduct > 0; i++) {
      const batch = batches[i];
      const deductFromBatch = Math.min(batch.quantityRemaining, remainingToDeduct);
      
      batchesUsed.push({
        batchId: batch.batchId,
        purchaseDate: batch.purchaseDate,
        purchaseInvoiceId: batch.purchaseInvoiceId,
        purchaseInvoiceNumber: batch.purchaseInvoiceNumber,
        costPrice: batch.costPrice,
        retailPrice: batch.retailPrice || 0,
        wholesalePrice: batch.wholesalePrice || 0,
        discount: batch.discount || 0, // Include discount in batch usage tracking
        quantityUsed: deductFromBatch,
        totalCost: batch.costPrice * deductFromBatch,
        supplier: batch.supplier,
        createdAt: batch.createdAt
      });
      
      batch.quantityRemaining -= deductFromBatch;
      remainingToDeduct -= deductFromBatch;
    }
    
    if (remainingToDeduct > 0) {
      return { 
        success: false, 
        error: `Insufficient stock. Need ${quantityToDeduct}, available ${quantityToDeduct - remainingToDeduct}`,
        batchesUsed: []
      };
    }
    
    // Keep depleted batches for history — do NOT filter them out
    // Calculate new totals
    const totalStock = batches.reduce((sum, b) => sum + b.quantityRemaining, 0);
    const totalCost = batches.reduce((sum, b) => sum + (b.costPrice * b.quantityRemaining), 0);
    const averageCost = totalStock > 0 ? totalCost / totalStock : 0;
    
    // Update inventory
    await updateDoc(itemRef, {
      batches: batches,
      stock: totalStock,
      averageCost: averageCost,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, batchesUsed: batchesUsed };
  } catch (error) {
    console.error('Error deducting stock FIFO:', error);
    return { success: false, error: error.message, batchesUsed: [] };
  }
};

/**
 * Get stock history for an item
 */
export const getStockHistory = async (itemId) => {
  try {
    const historyRef = collection(db, 'inventory', itemId, 'stockHistory');
    const q = query(historyRef, orderBy('date', 'desc'));
    const snapshot = await getDocs(q);
    
    const history = [];
    snapshot.forEach(doc => {
      history.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: history };
  } catch (error) {
    console.error('Error getting stock history:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Subscribe to stock history (real-time)
 */
export const subscribeToStockHistory = (itemId, callback) => {
  try {
    const historyRef = collection(db, 'inventory', itemId, 'stockHistory');
    const q = query(historyRef, orderBy('date', 'desc'));
    
    return onSnapshot(q, (snapshot) => {
      const history = [];
      snapshot.forEach(doc => {
        history.push({ id: doc.id, ...doc.data() });
      });
      callback(history);
    }, (error) => {
      console.error('Error subscribing to stock history:', error);
      callback([]);
    });
  } catch (error) {
    console.error('Error setting up stock history subscription:', error);
    return () => {};
  }
};

/**
 * Restore stock to specific batches (for bill deletion/cancellation)
 * Recreates batches if they were fully depleted
 */
export const restoreStockFIFO = async (itemId, batchesUsed) => {
  try {
    const itemRef = doc(db, 'inventory', itemId);
    const itemSnap = await getDoc(itemRef);
    
    if (!itemSnap.exists()) {
      return { success: false, error: 'Item not found' };
    }
    
    const itemData = itemSnap.data();
    let batches = itemData.batches || [];
    
    // Restore quantities to each batch
    for (const usedBatch of batchesUsed) {
      // Find existing batch
      const existingBatchIndex = batches.findIndex(b => b.batchId === usedBatch.batchId);
      
      if (existingBatchIndex >= 0) {
        // Batch still exists, restore quantity
        batches[existingBatchIndex].quantityRemaining += usedBatch.quantityUsed;
      } else {
        // Batch was fully depleted and removed, recreate it
        const restoredBatch = {
          batchId: usedBatch.batchId,
          purchaseInvoiceId: usedBatch.purchaseInvoiceId || 'N/A',
          purchaseInvoiceNumber: usedBatch.purchaseInvoiceNumber || 'N/A',
          purchaseDate: usedBatch.purchaseDate || new Date().toISOString().split('T')[0],
          costPrice: usedBatch.costPrice,
          retailPrice: usedBatch.retailPrice || 0,
          wholesalePrice: usedBatch.wholesalePrice || 0,
          discount: usedBatch.discount || 0, // Restore discount
          quantityPurchased: usedBatch.quantityUsed, // We only know what was used
          quantityRemaining: usedBatch.quantityUsed,
          supplier: usedBatch.supplier || 'N/A',
          createdAt: usedBatch.createdAt || new Date().toISOString(),
          restored: true // Mark as restored for tracking
        };
        batches.push(restoredBatch);
        console.log(`Recreated batch ${usedBatch.batchId} with purchase date: ${restoredBatch.purchaseDate}`);
      }
    }
    
    // Sort batches by createdAt timestamp (oldest first) - more precise than purchaseDate
    batches = batches.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    
    // Calculate new totals
    const totalStock = batches.reduce((sum, b) => sum + b.quantityRemaining, 0);
    const totalCost = batches.reduce((sum, b) => sum + (b.costPrice * b.quantityRemaining), 0);
    const averageCost = totalStock > 0 ? totalCost / totalStock : 0;
    
    // Update inventory
    await updateDoc(itemRef, {
      batches: batches,
      stock: totalStock,
      averageCost: averageCost,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, batchesRestored: batchesUsed.length };
  } catch (error) {
    console.error('Error restoring stock FIFO:', error);
    return { success: false, error: error.message };
  }
};

const stockBatchService = {
  getStockBatches,
  addStockBatch,
  deductStockFIFO,
  restoreStockFIFO,
  getStockHistory,
  subscribeToStockHistory
};

export default stockBatchService;
